import {
  type JSX,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  Controller,
  useWatch,
  type Control,
  type UseFormSetValue,
} from "react-hook-form";
import { Trash2 } from "lucide-react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  cn,
  useZodForm,
} from "@fluid-app/ui-primitives";
import { FormTextField } from "./form-fields/FormTextField";
import { FormComboboxField } from "./form-fields/FormComboboxField";
import { PhoneInput } from "@fluid-app/ui-components/components/PhoneInput";
import type { fluidPay } from "@fluid-app/fluid-pay-core";
import { useCountryStates, type State } from "@fluid-app/fluid-pay-core";
import { useAddressFieldsApiOptional } from "@fluid-app/address-fields/address-fields-api-context";
import type { AddressField } from "@fluid-app/address-fields/types";

const addressFormSchema = z.object({
  name: z.string(),
  address1: z.string(),
  address2: z.string().optional().nullable(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  phone: z.string().optional().nullable(),
  country_code: z.string().min(1, "Country is required"),
  default: z.boolean(),
});

export type AddressFormData = z.infer<typeof addressFormSchema>;

interface CountryOption {
  iso: string;
  name: string;
}

export interface AddressAutocompleteRenderProps {
  control: Control<AddressFormData>;
  setValue: UseFormSetValue<AddressFormData>;
  countryCode: string;
  stateOptions: readonly State[];
}

interface AddressFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAddress: fluidPay.CustomerAddress | null;
  onSubmit: (data: fluidPay.CreateAddressBody) => void;
  isSubmitting: boolean;
  countries?: CountryOption[];
  error?: string;
  renderAddressAutocomplete?: (
    props: AddressAutocompleteRenderProps,
  ) => ReactNode;
  fetchStates?: (countryCode: string) => Promise<State[]>;
  t: (key: string) => string;
  onDelete?: () => void;
  isDeleting?: boolean;
  defaultCountry?: string;
  languageIso?: string;
}

type AddressFieldName = keyof Omit<AddressFormData, "default" | "country_code">;

const STATIC_FIELD_ORDER: AddressFieldName[] = [
  "name",
  "address1",
  "address2",
  "city",
  "state",
  "postal_code",
  "phone",
];

const STATIC_LABELS: Record<AddressFieldName, string> = {
  name: "name",
  address1: "address_line_1",
  address2: "address_line_2",
  city: "city",
  state: "state",
  postal_code: "zip_code",
  phone: "phone_number",
};

const DEFAULT_HIDDEN_FIELDS = new Set<AddressFieldName>(["phone"]);
const STATIC_REQUIRED_FIELDS = new Set<AddressFieldName>([
  "name",
  "address1",
  "city",
  "state",
  "postal_code",
]);
const REQUIRED_FIELD_MESSAGES: Record<AddressFieldName, string> = {
  name: "Name is required",
  address1: "Address line 1 is required",
  address2: "Address line 2 is required",
  city: "City is required",
  state: "State is required",
  postal_code: "Postal code is required",
  phone: "Phone is required",
};
const AUTOCOMPLETE_FIELD_PREFIX: readonly AddressFieldName[] = [
  "name",
  "address1",
  "address2",
];

function isAddressFieldName(value: string): value is AddressFieldName {
  return STATIC_FIELD_ORDER.includes(value as AddressFieldName);
}

function prioritizeAutocompleteFields(
  fields: readonly AddressFieldName[],
): AddressFieldName[] {
  const prefix = AUTOCOMPLETE_FIELD_PREFIX.filter((field) =>
    fields.includes(field),
  );
  const rest = fields.filter(
    (field) => !AUTOCOMPLETE_FIELD_PREFIX.includes(field),
  );
  return [...prefix, ...rest];
}

function groupFields(fields: AddressFieldName[]): AddressFieldName[][] {
  const groups: AddressFieldName[][] = [];
  let i = 0;
  while (i < fields.length) {
    const current = fields[i]!;

    const locationFields = new Set<AddressFieldName>([
      "city",
      "state",
      "postal_code",
    ]);
    if (locationFields.has(current)) {
      const group: AddressFieldName[] = [current];
      while (
        i + group.length < fields.length &&
        locationFields.has(fields[i + group.length]!)
      ) {
        group.push(fields[i + group.length]!);
      }
      if (group.length > 1) {
        groups.push(group);
        i += group.length;
        continue;
      }
    }

    groups.push([current]);
    i++;
  }
  return groups;
}

function isFieldRequired(
  bffFields: readonly AddressField[] | null,
  fieldName: AddressFieldName,
): boolean {
  if (!bffFields) return STATIC_REQUIRED_FIELDS.has(fieldName);
  return (
    bffFields.find((field) => field.field === fieldName)?.required ?? false
  );
}

function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

export default function AddressFormDialog({
  isOpen,
  onClose,
  selectedAddress,
  onSubmit,
  isSubmitting,
  countries = [],
  error,
  renderAddressAutocomplete,
  fetchStates,
  t,
  onDelete,
  isDeleting = false,
  defaultCountry = "US",
  languageIso,
}: AddressFormDialogProps): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const phoneInputId = useId();
  const addressFieldsApi = useAddressFieldsApiOptional();
  const [bffFields, setBffFields] = useState<AddressField[] | null>(null);

  useEffect(() => {
    if (!isOpen) setConfirmDelete(false);
  }, [isOpen]);

  useEffect(() => {
    if (!confirmDelete) return;
    const timeoutId = setTimeout(() => setConfirmDelete(false), 3000);
    return () => clearTimeout(timeoutId);
  }, [confirmDelete]);

  const deleteButtonLabel = useMemo(() => {
    if (isDeleting) return t("deleting");
    if (confirmDelete) return t("tap_to_confirm");
    return t("delete_address");
  }, [isDeleting, confirmDelete, t]);

  const { control, handleSubmit, reset, setValue, setError, clearErrors } =
    useZodForm<AddressFormData>(addressFormSchema, {
      defaultValues: {
        name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postal_code: "",
        phone: "",
        country_code: defaultCountry,
        default: false,
      },
    });

  const selectedCountryCode = useWatch({ control, name: "country_code" });
  const isDefault = useWatch({ control, name: "default" });

  const { stateOptions, config, handleCountryChange } = useCountryStates();
  const hasAddressAutocomplete = Boolean(renderAddressAutocomplete);

  useEffect(() => {
    if (!addressFieldsApi || !selectedCountryCode) return;
    let cancelled = false;
    setBffFields(null);
    addressFieldsApi
      .fetchAddressFields(selectedCountryCode, languageIso)
      .then((response) => {
        if (!cancelled) {
          setBffFields([...response.address_fields.fields]);
        }
      })
      .catch(() => {
        if (!cancelled) setBffFields(null);
      });
    return () => {
      cancelled = true;
    };
  }, [addressFieldsApi, selectedCountryCode, languageIso]);

  const fieldOrder = useMemo<AddressFieldName[]>(() => {
    if (!bffFields)
      return STATIC_FIELD_ORDER.filter((f) => !DEFAULT_HIDDEN_FIELDS.has(f));
    const fieldOrder = bffFields.map((f) => f.field).filter(isAddressFieldName);
    return hasAddressAutocomplete
      ? prioritizeAutocompleteFields(fieldOrder)
      : fieldOrder;
  }, [bffFields, hasAddressAutocomplete]);

  const fieldGroups = useMemo(() => groupFields(fieldOrder), [fieldOrder]);

  const getFieldLabel = (fieldName: AddressFieldName): string => {
    if (bffFields) {
      const bffField = bffFields.find((f) => f.field === fieldName);
      if (bffField) return bffField.label;
    }
    if (fieldName === "state" && config?.regionLabel) {
      return t(config.regionLabel);
    }
    if (fieldName === "postal_code" && config?.postalLabel) {
      return t(config.postalLabel);
    }
    const key = STATIC_LABELS[fieldName];
    return key ? t(key) : fieldName;
  };

  const countrySelectOptions = [...countries]
    .map((country) => ({
      name: country.name,
      value: country.iso,
    }))
    .sort((a, b) => {
      if (a.value === selectedCountryCode) return -1;
      if (b.value === selectedCountryCode) return 1;
      return a.name.localeCompare(b.name);
    });

  const stateSelectOptions = stateOptions.map((state) => ({
    name: state.name,
    value: state.isoCode,
  }));

  useEffect(() => {
    if (isOpen) {
      if (selectedAddress) {
        reset({
          name: selectedAddress.name ?? "",
          address1: selectedAddress.address1,
          address2: selectedAddress.address2 ?? "",
          city: selectedAddress.city,
          state: selectedAddress.state,
          postal_code: selectedAddress.postal_code,
          phone: selectedAddress.phone ?? "",
          country_code: selectedAddress.country_code ?? defaultCountry,
          default: selectedAddress.default,
        });
        handleCountryChange(
          selectedAddress.country_code ?? defaultCountry,
          fetchStates,
        );
      } else {
        reset({
          name: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          postal_code: "",
          phone: "",
          country_code: defaultCountry,
          default: false,
        });
        handleCountryChange(defaultCountry, fetchStates);
      }
    }
    // defaultCountry intentionally omitted: a background addresses refetch can
    // change the parent-computed value while the dialog is open, which would
    // wipe the user's in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress, isOpen, reset, handleCountryChange, fetchStates]);

  useEffect(() => {
    handleCountryChange(selectedCountryCode, fetchStates);
  }, [selectedCountryCode, handleCountryChange, fetchStates]);

  const handleFormSubmit = handleSubmit((data) => {
    clearErrors(STATIC_FIELD_ORDER);
    const missingRequiredFields = fieldOrder.filter(
      (fieldName) =>
        isFieldRequired(bffFields, fieldName) && isBlank(data[fieldName]),
    );

    if (missingRequiredFields.length > 0) {
      for (const fieldName of missingRequiredFields) {
        setError(fieldName, {
          type: "manual",
          message: REQUIRED_FIELD_MESSAGES[fieldName],
        });
      }
      return;
    }

    const city = bffFields && !fieldOrder.includes("city") ? "" : data.city;
    const state = bffFields && !fieldOrder.includes("state") ? "" : data.state;

    onSubmit({
      address: {
        name: data.name,
        address1: data.address1,
        address2: data.address2 ?? null,
        city,
        state,
        postal_code: data.postal_code,
        phone: data.phone || null,
        country_code: data.country_code,
        default: data.default,
      },
    });
  });

  const renderField = (
    fieldName: AddressFieldName,
    containerClassName?: string,
  ): JSX.Element => {
    const label = getFieldLabel(fieldName);

    if (fieldName === "state" && stateSelectOptions.length > 0) {
      return (
        <FormComboboxField
          key={fieldName}
          control={control}
          name="state"
          label={label}
          placeholder={t("select_state")}
          options={stateSelectOptions}
          containerClassName={containerClassName}
          disabled={isSubmitting}
        />
      );
    }

    if (fieldName === "address1" && renderAddressAutocomplete) {
      return (
        <div key={fieldName} className={cn("space-y-1", containerClassName)}>
          <Label
            htmlFor={fieldName}
            className="mb-1.5 block text-sm font-medium"
          >
            {label}
          </Label>
          {renderAddressAutocomplete({
            control,
            setValue,
            countryCode: selectedCountryCode,
            stateOptions,
          })}
        </div>
      );
    }

    if (fieldName === "phone") {
      return (
        <div key={fieldName} className={containerClassName}>
          <Controller
            control={control}
            name="phone"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className="space-y-1">
                <Label
                  htmlFor={phoneInputId}
                  className="mb-1.5 block text-sm font-medium"
                >
                  {label}
                </Label>
                <PhoneInput
                  id={phoneInputId}
                  value={value ?? ""}
                  onChange={onChange}
                  defaultCountry={selectedCountryCode}
                  error={!!error}
                  disabled={isSubmitting}
                  searchPlaceholder={t("search_country")}
                  emptyText={t("no_results_found")}
                />
                {error?.message && (
                  <p className="text-destructive text-sm">{error.message}</p>
                )}
              </div>
            )}
          />
        </div>
      );
    }

    return (
      <FormTextField
        key={fieldName}
        control={control}
        name={fieldName}
        label={label}
        containerClassName={containerClassName}
        disabled={isSubmitting}
        autoComplete={fieldName === "name" ? "name" : undefined}
      />
    );
  };

  const isEditMode = selectedAddress !== null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[90vh] max-w-md overflow-y-auto rounded md:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">
            {isEditMode ? t("edit_address") : t("add_an_address")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <FormComboboxField
            control={control}
            name="country_code"
            label={t("country")}
            placeholder={t("select_an_option")}
            options={countrySelectOptions}
            disabled={isSubmitting}
          />
          {fieldGroups.map((group) => {
            if (group.length === 1) {
              return renderField(group[0]!);
            }
            return (
              <div key={group.join("-")} className="flex gap-3">
                {group.map((fieldName) => renderField(fieldName, "flex-1"))}
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          {onDelete ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (confirmDelete) {
                  onDelete();
                } else {
                  setConfirmDelete(true);
                }
              }}
              disabled={isSubmitting || isDeleting}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="size-4" />
              {deleteButtonLabel}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-x-2">
              <input
                type="checkbox"
                id="set_as_default_address"
                checked={isDefault}
                onChange={() => setValue("default", !isDefault)}
                disabled={isSubmitting || isDeleting}
                className="size-4"
              />
              <label htmlFor="set_as_default_address" className="text-sm">
                {t("set_as_default_address")}
              </label>
            </div>

            <Button
              type="submit"
              onClick={handleFormSubmit}
              disabled={isSubmitting || isDeleting}
            >
              {isSubmitting && (
                <div className="mr-2 size-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
              )}
              {isSubmitting ? t("saving") : t("save_address")}
            </Button>
          </div>
        </div>

        {error && <div className="text-destructive mt-2 text-sm">{error}</div>}
      </DialogContent>
    </Dialog>
  );
}
